import React, { useContext, useState } from "react";
import {
  makeStyles,
  Paper,
  Typography,
  Button,
  Box,
  IconButton,
  Avatar,
  Tooltip,
  Chip,
  Grid,
  TextField
} from "@material-ui/core";
import {
  Description as FileIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  Event as EventIcon,
  Person as PersonIcon,
  GetApp as DownloadIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon
} from "@material-ui/icons";
import { toast } from "react-toastify";
import api from "../../../services/api";
import toastError from "../../../errors/toastError";
import { AuthContext } from "../../../context/Auth/AuthContext";
import ConfirmationModal from "../../../components/ConfirmationModal";
import ModalImageCors from "../../../components/ModalImageCors";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3),
  },
  fileCard: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    border: "1px solid " + theme.palette.divider,
    borderRadius: 12,
    transition: "box-shadow 0.3s ease, border-color 0.3s ease", 
    backgroundColor: theme.palette.background.paper,
    "&:hover": {
      boxShadow: theme.shadows[4],
      borderColor: theme.palette.primary.main,
    },
  },
  fileContentWrapper: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    width: "100%",
  },
  previewContainer: {
    width: 80,
    height: 80,
    flexShrink: 0,
    borderRadius: 8,
    overflow: "hidden", 
    marginRight: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.palette.action.hover,
    border: "1px solid " + theme.palette.divider,
    "& img": {
        objectFit: "cover !important",
    }
  },
  infoContainer: {
    flexGrow: 1,
    minWidth: 0,
  },
  fileName: {
    fontWeight: 600,
    fontSize: "1rem",
    marginBottom: 4,
    color: theme.palette.text.primary,
    wordBreak: "break-word",
  },
  metaDataRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: "0.85rem",
  },
  appointmentBadge: {
    marginTop: theme.spacing(1.5),
    display: "flex",
    alignItems: "center",
    backgroundColor: theme.palette.action.selected,
    padding: "6px 12px",
    borderRadius: 6,
    borderLeft: "4px solid " + theme.palette.primary.main,
    width: "fit-content",
  },
  appointmentText: {
    fontSize: "0.85rem",
    color: theme.palette.text.primary,
    fontWeight: 500,
  },
  appointmentSubText: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginLeft: theme.spacing(1),
  },
  actionButtons: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    marginLeft: theme.spacing(1),
  },
  avatarSmall: {
    width: 20,
    height: 20,
    fontSize: 10,
    marginRight: 6,
    backgroundColor: theme.palette.secondary.main,
  },
}));

const AttachmentsGallery = ({ files, onUploadClick, onFileDeleted, onFileUpdated }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [selectedFileToDelete, setSelectedFileToDelete] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [updating, setUpdating] = useState(false);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

  const isAdmin = user.profile === "admin" || user.profile === "superadmin";

  const handleDeleteClick = (file) => {
    setSelectedFileToDelete(file);
    setConfirmModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedFileToDelete) return;
    try {
      await api.delete("/medical-history/files/" + selectedFileToDelete.id);
      if (onFileDeleted) onFileDeleted(selectedFileToDelete.id);
      setConfirmModalOpen(false);
      setSelectedFileToDelete(null);
    } catch (err) {
      toastError(err);
    }
  };

  const handleStartEdit = (file) => {
    setEditingId(file.id);
    setEditingName(file.name);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingName("");
  };

  const handleSaveEdit = async () => {
    if (!editingName.trim()) return;
    
    setUpdating(true);
    try {
      await api.put(`/medical-history/files/${editingId}`, {
        name: editingName
      });
      toast.success("Nombre del archivo actualizado correctamente"); 
      if (onFileUpdated) onFileUpdated(); 
      
      setEditingId(null);
      setEditingName("");
    } catch (err) {
      toastError(err);
    } finally {
      setUpdating(false);
    }
  };
  
  const handleFileClick = (file) => {
    const url = backendUrl + "/public" + file.path;
    if (!file.mediaType.includes("image")) {
      window.open(url, "_blank");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleString(undefined, {
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false
    });
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name.replace(/\s+/, " ").split(" ").slice(0, 2).map(function(v) { return v[0].toUpperCase(); }).join("");
  };

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Box display="flex" alignItems="center">
            <Typography variant="h6" style={{ fontWeight: 600 }}>
            Archivos Adjuntos
            </Typography>
            <Chip 
                label={files.length} 
                size="small" 
                style={{ marginLeft: 10, fontWeight: "bold" }} 
            />
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<UploadIcon />}
          onClick={onUploadClick}
        >
          Subir Archivo
        </Button>
      </div>

      {files.length === 0 ? (
        <Paper variant="outlined" style={{ padding: 40, textAlign: "center", borderStyle: "dashed" }}>
          <UploadIcon style={{ fontSize: 60, color: "#ccc", marginBottom: 10 }} />
          <Typography color="textSecondary">
            No hay archivos adjuntos en el historial médico.
          </Typography>
        </Paper>
      ) : (
        <div>
          {files.map((file) => {
            const fileUrl = backendUrl + "/public" + file.path;
            const isImage = file.mediaType.includes("image");
            const isPdf = file.mediaType.includes("pdf");
            const userName = (file.user && file.user.name) || "Sistema";

            const hasAppointment = !!file.appointmentId;
            const motivoConsulta = file.appointment && file.appointment.motivoConsulta;

            return (
              <Paper key={file.id} className={classes.fileCard} elevation={0}>
                <div className={classes.fileContentWrapper}>
                  <div className={classes.previewContainer}>
                    {isImage ? (
                      <ModalImageCors imageUrl={fileUrl} />
                    ) : (
                      <IconButton onClick={() => handleFileClick(file)}>
                        {isPdf ? (
                            <PdfIcon style={{ fontSize: 40, color: "#f44336" }} />
                        ) : (
                            <FileIcon style={{ fontSize: 40, color: "#1976d2" }} />
                        )}
                      </IconButton>
                    )}
                  </div>

                  <div className={classes.infoContainer}>
                    {editingId === file.id ? (
                      <Box mb={1} mr={2}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          size="small"
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          autoFocus
                          disabled={updating}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleSaveEdit();
                            if (e.key === "Escape") handleCancelEdit();
                          }}
                        />
                      </Box>
                    ) : (
                      <Typography
                        className={classes.fileName}
                        component="a"
                        href={!isImage ? fileUrl : "#"}
                        target={!isImage ? "_blank" : "_self"}
                        onClick={(e) => { if (isImage) e.preventDefault(); }}
                        style={{ textDecoration: "none", cursor: isImage ? "default" : "pointer" }}
                      >
                        {file.name}
                      </Typography>
                    )}

                    <div className={classes.metaDataRow}>
                        <Box display="flex" alignItems="center" mr={2}>
                            <Avatar className={classes.avatarSmall} alt={userName}>
                                {getInitials(userName)}
                            </Avatar>
                            <Typography variant="caption" style={{ fontWeight: 500 }}>
                                {userName}
                            </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" color="textSecondary">
                            <Typography variant="caption">
                                {formatDate(file.createdAt)}
                            </Typography>
                        </Box>
                    </div>

                    {hasAppointment && (
                        <div className={classes.appointmentBadge}>
                            <EventIcon style={{ fontSize: 18, color: "inherit", marginRight: 6, opacity: 0.7 }} />
                            <div>
                                <span className={classes.appointmentText}>
                                    Cita #{file.appointmentId}
                                </span>
                                {motivoConsulta && (
                                    <span className={classes.appointmentSubText}>
                                         • Motivo: {motivoConsulta.length > 50 ? motivoConsulta.substring(0, 50) + "..." : motivoConsulta}
                                    </span>
                                )}
                            </div>
                        </div>
                    )}
                  </div>

                  <div className={classes.actionButtons}>
                    {editingId === file.id ? (
                      <>
                        <Tooltip title="Guardar">
                          <IconButton
                            size="small"
                            onClick={handleSaveEdit}
                            style={{ color: "#4caf50", marginBottom: 4 }}
                            disabled={updating}
                          >
                            <CheckIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancelar">
                          <IconButton
                            size="small"
                            onClick={handleCancelEdit}
                            style={{ color: "#f44336" }}
                            disabled={updating}
                          >
                            <CloseIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : (
                      <>
                        {!isImage && (
                          <Tooltip title="Descargar / Abrir">
                            <IconButton size="small" onClick={() => handleFileClick(file)}>
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}

                        {(isAdmin || file.userId === user.id) && (
                          <Tooltip title="Editar nombre">
                            <IconButton
                              size="small"
                              onClick={() => handleStartEdit(file)}
                              style={{ marginTop: 8 }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}

                        {isAdmin && (
                          <Tooltip title="Eliminar archivo">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(file)}
                              style={{ color: "#f44336", marginTop: 8 }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </Paper>
            );
          })}
        </div>
      )}

      <ConfirmationModal
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Eliminar Archivo"
      >
        ¿Estás seguro de que deseas eliminar este archivo? Esta acción no se puede deshacer.
      </ConfirmationModal>
    </div>
  );
};

export default AttachmentsGallery;